import React, { useState } from 'react';
import './styles/ApiDocsPage.css';
import toast from 'react-hot-toast';

type LanguageKey = 'curl' | 'javascript' | 'python' | 'csharp';

const ApiDocsPage: React.FC = () => {
    const [activeLanguage, setActiveLanguage] = useState<LanguageKey>('curl');

    // Adjust this to your actual host
    const BASE_URL = 'https://api.cutora.io';
    const URL_ENDPOINT = '/api/v1/files/download/url';
    const FILE_ENDPOINT = '/api/v1/files/download/file';

    // ---- Code snippets: Download from URL (JSON) ----
    const codeSnippetsUrl: Record<LanguageKey, string> = {
        curl: `curl -X POST ${BASE_URL}${URL_ENDPOINT} \\
  -H "X-Api-Key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "url": "https://example.com/...",
    "duration": {
      "start": "00:00:05",
      "end": "00:01:30"
    },
    "outputFormat": 0,
    "notifyByEmail": true,
    "outputFileName": "my-audio-file"
  }'
# outputFormat enum:
# 0 = MP3, 1 = MP4, 2 = WAV, 3 = AAC, 4 = MKV,
# 5 = WEBM, 6 = AVI, 7 = MOV, 8 = M4A`,

        javascript: `// Node.js example (Node 18+ fetch or node-fetch)
async function downloadFromUrl() {
  const response = await fetch("${BASE_URL}${URL_ENDPOINT}", {
    method: "POST",
    headers: {
      "X-Api-Key": "YOUR_API_KEY",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      url: "https://example.com/...",
      duration: {
        start: "00:00:05", // HH:MM:SS
        end: "00:01:30",
      },
      outputFormat: 0, // 0 = MP3, 1 = MP4, ...
      notifyByEmail: true,
      outputFileName: "my-audio-file",
    }),
  });

  if (!response.ok) {
    console.error("Request failed", await response.text());
    return;
  }

  const data = await response.json();
  console.log("Job accepted:", data); // { jobId: "..." }
}
`,

        python: `import requests

def download_from_url():
    url = "${BASE_URL}${URL_ENDPOINT}"
    headers = {
        "X-Api-Key": "YOUR_API_KEY",
        "Content-Type": "application/json",
    }
    payload = {
        "url": "https://example.com/video...",
        "duration": {
            "start": "00:00:05",  # HH:MM:SS
            "end": "00:01:30",
        },
        "outputFormat": 0,  # 0 = MP3, 1 = MP4, ...
        "notifyByEmail": True,
        "outputFileName": "my-audio-file",
    }

    response = requests.post(url, json=payload, headers=headers)
    print(response.status_code, response.json())  # { "jobId": "..." }
`,

        csharp: `using System.Net.Http;
using System.Net.Http.Json;
using System.Threading.Tasks;

public class DownloadFileRequestModel
{
    public string Url { get; set; }
    public DurationModel Duration { get; set; }
    public int OutputFormat { get; set; } // enum value: 0 = MP3, 1 = MP4, ...
    public bool NotifyByEmail { get; set; }
    public string OutputFileName { get; set; }
}

public class DurationModel
{
    public string Start { get; set; } // "HH:MM:SS"
    public string End { get; set; }
}

public async Task CallDownloadFromUrlAsync()
{
    using var client = new HttpClient();
    client.DefaultRequestHeaders.Add("X-Api-Key", "YOUR_API_KEY");

    var body = new DownloadFileRequestModel
    {
        Url = "https://example.com/...",
        Duration = new DurationModel
        {
            Start = "00:00:05",
            End = "00:01:30"
        },
        OutputFormat = 0, // MP3
        NotifyByEmail = true,
        OutputFileName = "my-audio-file"
    };

    var response = await client.PostAsJsonAsync("${BASE_URL}${URL_ENDPOINT}", body);
    var content = await response.Content.ReadAsStringAsync();
    System.Console.WriteLine(content); // { "jobId": "..." }
}
`,
    };

    // ---- Code snippets: Upload file (multipart/form-data) ----
    const codeSnippetsFile: Record<LanguageKey, string> = {
        curl: `curl -X POST ${BASE_URL}${FILE_ENDPOINT} \\
  -H "X-Api-Key: YOUR_API_KEY" \\
  -F "file=@/path/to/local/file.mp4" \\
  -F "duration.start=00:00:05" \\
  -F "duration.end=00:01:30" \\
  -F "outputFormat=0" \\
  -F "notifyByEmail=true" \\
  -F "outputFileName=my-audio-file"`,

        javascript: `// Node.js example using form-data
import fetch from "node-fetch";
import FormData from "form-data";
import fs from "fs";

async function uploadFile() {
  const form = new FormData();
  form.append("file", fs.createReadStream("/path/to/local/file.mp4"));
  form.append("duration.start", "00:00:05");
  form.append("duration.end", "00:01:30");
  form.append("outputFormat", "0"); // 0 = MP3
  form.append("notifyByEmail", "true");
  form.append("outputFileName", "my-audio-file");

  const res = await fetch("${BASE_URL}${FILE_ENDPOINT}", {
    method: "POST",
    headers: {
      "X-Api-Key": "YOUR_API_KEY",
      ...form.getHeaders(),
    },
    body: form,
  });

  if (!res.ok) {
    console.error("Request failed", await res.text());
    return;
  }

  const data = await res.json();
  console.log("Job accepted:", data); // { jobId: "..." }
}
`,

        python: `import requests

def upload_file():
    url = "${BASE_URL}${FILE_ENDPOINT}"
    headers = {
        "X-Api-Key": "YOUR_API_KEY",
    }

    files = {
        "file": open("/path/to/local/file.mp4", "rb"),
    }

    data = {
        "duration.start": "00:00:05",
        "duration.end": "00:01:30",
        "outputFormat": "0",
        "notifyByEmail": "true",
        "outputFileName": "my-audio-file",
    }

    response = requests.post(url, headers=headers, files=files, data=data)
    print(response.status_code, response.json())  # { "jobId": "..." }
`,

        csharp: `using System.IO;
using System.Net.Http;
using System.Threading.Tasks;

public async Task UploadFileAsync()
{
    using var client = new HttpClient();
    client.DefaultRequestHeaders.Add("X-Api-Key", "YOUR_API_KEY");

    using var form = new MultipartFormDataContent();

    var fileStream = File.OpenRead("/path/to/local/file.mp4");
    form.Add(new StreamContent(fileStream), "file", "file.mp4");

    form.Add(new StringContent("00:00:05"), "duration.start");
    form.Add(new StringContent("00:01:30"), "duration.end");
    form.Add(new StringContent("0"), "outputFormat"); // 0 = MP3
    form.Add(new StringContent("true"), "notifyByEmail");
    form.Add(new StringContent("my-audio-file"), "outputFileName");

    var response = await client.PostAsync("${BASE_URL}${FILE_ENDPOINT}", form);
    var content = await response.Content.ReadAsStringAsync();
    System.Console.WriteLine(content); // { "jobId": "..." }
}
`,
    };

    const webhookCompletedExample = `POST https://your-webhook-url.com/media-events
Content-Type: application/json

{
  "jobId": "aabbccdd-eeff-0011-2233-445566778899",
  "eventType": "file.completed",
  "outputUrl": "https://cdn.yourdomain.com/outputs/aabbccdd-eeff-0011-2233-445566778899.mp3"
}`;

    const webhookFailedExample = `POST https://your-webhook-url.com/media-events
Content-Type: application/json

{
  "jobId": "aabbccdd-eeff-0011-2233-445566778899",
  "eventType": "file.failed",
  "failReason": "Transcoding failed due to unsupported codec."
}`;

    const handleCopy = async (snippetMap: Record<LanguageKey, string> | string) => {
        try {
            if (typeof snippetMap === 'string') {
                await navigator.clipboard.writeText(snippetMap);
            } else {
                await navigator.clipboard.writeText(snippetMap[activeLanguage]);
            }

            toast('Copied to clipboard');
        } catch {
        }
    };

    return (
        <div className="api-docs">
            <header className="api-docs-header">
                <div>
                    <h1 className="api-docs-title">Public Files API</h1>
                    <p className="api-docs-subtitle">
                        Download media from a URL or upload your own file, convert to multiple formats,
                        and receive completion events via webhooks.
                    </p>
                </div>
                <div className="api-docs-header-pill">
                    <span className="api-docs-pill api-docs-pill--primary">v1</span>
                    <span className="api-docs-pill api-docs-pill--outline">Public API</span>
                </div>
            </header>

            <div className="api-docs-layout">
                <nav className="api-docs-sidebar">
                    <div className="api-docs-sidebar-section">
                        <p className="api-docs-sidebar-label">Getting started</p>
                        <ul>
                            <li><a href="#overview">Overview</a></li>
                            <li><a href="#auth">Authentication</a></li>
                            <li><a href="#flow">Job & webhook flow</a></li>
                        </ul>
                    </div>

                    <div className="api-docs-sidebar-section">
                        <p className="api-docs-sidebar-label">Endpoints</p>
                        <ul>
                            <li><a href="#endpoint-url">POST {URL_ENDPOINT}</a></li>
                            <li><a href="#endpoint-file">POST {FILE_ENDPOINT}</a></li>
                        </ul>
                    </div>

                    <div className="api-docs-sidebar-section">
                        <p className="api-docs-sidebar-label">Models</p>
                        <ul>
                            <li><a href="#model-download-url">DownloadFileRequestModel</a></li>
                            <li><a href="#model-download-upload">DownloadUploadFileRequestModel</a></li>
                            <li><a href="#model-duration">DurationModel</a></li>
                            <li><a href="#model-output-format">OutputFormatRequestModel</a></li>
                            <li><a href="#model-webhook">WebhookFileCompletedPayload</a></li>
                            <li><a href="#model-webhook-failed">WebhookFileFailedPayload</a></li>
                        </ul>
                    </div>
                </nav>

                <main className="api-docs-content">
                    {/* Overview */}
                    <section id="overview" className="api-docs-section">
                        <h2>Overview</h2>
                        <p>
                            The Public Files API is an asynchronous media processing API. You send either:
                        </p>
                        <ul className="api-docs-list">
                            <li>
                                a remote media <strong>URL</strong>, or
                            </li>
                            <li>
                                an uploaded <strong>file</strong> via <code className="api-docs-inline-code">multipart/form-data</code>,
                            </li>
                        </ul>
                        <p>
                            and receive a <code className="api-docs-inline-code">jobId</code> in response. When the job
                            finishes, your configured webhook is called to notify you of success or failure.
                        </p>

                        <div className="api-docs-grid">
                            <div className="api-docs-card">
                                <h3>Base URL</h3>
                                <code className="api-docs-inline-code">{BASE_URL}</code>
                            </div>
                            <div className="api-docs-card">
                                <h3>Supported output formats</h3>
                                <p>
                                    <code className="api-docs-inline-code">MP3</code>,{' '}
                                    <code className="api-docs-inline-code">MP4</code>,{' '}
                                    <code className="api-docs-inline-code">WAV</code>,{' '}
                                    <code className="api-docs-inline-code">AAC</code>,{' '}
                                    <code className="api-docs-inline-code">MKV</code>,{' '}
                                    <code className="api-docs-inline-code">WEBM</code>,{' '}
                                    <code className="api-docs-inline-code">AVI</code>,{' '}
                                    <code className="api-docs-inline-code">MOV</code>,{' '}
                                    <code className="api-docs-inline-code">M4A</code>
                                </p>
                                <p className="api-docs-note">
                                    Sent as an <strong>enum int</strong> (0–8) in the request.
                                </p>
                            </div>
                        </div>
                    </section>

                    {/* Auth */}
                    <section id="auth" className="api-docs-section">
                        <h2>Authentication</h2>
                        <p>
                            All endpoints require an API key. In the examples we use an{' '}
                            <code className="api-docs-inline-code">X-Api-Key</code> header:
                        </p>
                        <div className="api-docs-card api-docs-card--code">
                            <pre className="api-docs-pre">
                                <code>X-Api-Key: YOUR_API_KEY</code>
                            </pre>
                        </div>
                    </section>

                    {/* Flow */}
                    <section id="flow" className="api-docs-section">
                        <h2>Job & webhook flow</h2>
                        <ol className="api-docs-steps">
                            <li>
                                Call <code className="api-docs-inline-code">POST {URL_ENDPOINT}</code> or{' '}
                                <code className="api-docs-inline-code">POST {FILE_ENDPOINT}</code>.
                            </li>
                            <li>
                                The API validates the payload and creates a job.
                            </li>
                            <li>
                                You receive <code className="api-docs-inline-code">{'{ jobId: "..." }'}</code> immediately.
                            </li>
                            <li>
                                When processing completes successfully, your webhook is called with{' '}
                                <code className="api-docs-inline-code">WebhookFileCompletedPayload</code>. On failures,
                                it is called with <code className="api-docs-inline-code">WebhookFileFailedPayload</code>.
                            </li>
                        </ol>
                    </section>

                    {/* Endpoint: Download from URL */}
                    <section id="endpoint-url" className="api-docs-section">
                        <div className="api-docs-section-header">
                            <h2>POST {URL_ENDPOINT}</h2>
                        </div>
                        <p>
                            Download and process media from a remote URL using a JSON request body (
                            <code className="api-docs-inline-code">DownloadFileRequestModel</code>).
                        </p>

                        <div className="api-docs-grid api-docs-grid--two">
                            <div className="api-docs-card">
                                <h3>Request body</h3>
                                <pre className="api-docs-pre">
                                    <code>
{`{
  "url": "https://example.com/...",
  "duration": {
    "start": "00:00:05",
    "end": "00:01:30"
  },
  "outputFormat": 0,
  "notifyByEmail": true,
  "outputFileName": "my-audio-file"
}`}
                                    </code>
                                </pre>
                            </div>
                            <div className="api-docs-card">
                                <h3>Response body</h3>
                                <pre className="api-docs-pre">
                                    <code>
{`{
  "jobId": "aabbccdd-eeff-0011-2233-445566778899"
}`}
                                    </code>
                                </pre>
                            </div>
                        </div>

                        <div className="api-docs-card api-docs-card--code">
                            <div className="api-docs-code-header">
                                <div className="api-docs-tabs">
                                    <button
                                        type="button"
                                        className={`api-docs-tab ${activeLanguage === 'curl' ? 'api-docs-tab--active' : ''}`}
                                        onClick={() => setActiveLanguage('curl')}
                                    >
                                        cURL
                                    </button>
                                    <button
                                        type="button"
                                        className={`api-docs-tab ${activeLanguage === 'javascript' ? 'api-docs-tab--active' : ''}`}
                                        onClick={() => setActiveLanguage('javascript')}
                                    >
                                        JavaScript
                                    </button>
                                    <button
                                        type="button"
                                        className={`api-docs-tab ${activeLanguage === 'python' ? 'api-docs-tab--active' : ''}`}
                                        onClick={() => setActiveLanguage('python')}
                                    >
                                        Python
                                    </button>
                                    <button
                                        type="button"
                                        className={`api-docs-tab ${activeLanguage === 'csharp' ? 'api-docs-tab--active' : ''}`}
                                        onClick={() => setActiveLanguage('csharp')}
                                    >
                                        C#
                                    </button>
                                </div>
                                <button
                                    type="button"
                                    className="api-docs-copy-btn"
                                    onClick={() => handleCopy(codeSnippetsUrl)}
                                >
                                    Copy
                                </button>
                            </div>
                            <pre className="api-docs-pre">
                                <code>{codeSnippetsUrl[activeLanguage]}</code>
                            </pre>
                        </div>
                    </section>

                    {/* Endpoint: Upload file */}
                    <section id="endpoint-file" className="api-docs-section">
                        <div className="api-docs-section-header">
                            <h2>POST {FILE_ENDPOINT}</h2>
                        </div>
                        <p>
                            Upload a local file for processing using <code className="api-docs-inline-code">
                                multipart/form-data
                            </code>{' '}
                            (<code className="api-docs-inline-code">DownloadUploadFileRequestModel</code>).
                        </p>

                        <div className="api-docs-grid api-docs-grid--two">
                            <div className="api-docs-card">
                                <h3>Form fields</h3>
                                <pre className="api-docs-pre">
                                    <code>
{`file             // required, binary
duration.start    // optional, "HH:MM:SS"
duration.end      // optional, "HH:MM:SS"
outputFormat      // required, enum int (0-8)
notifyByEmail     // optional, bool
outputFileName    // optional, string`}
                                    </code>
                                </pre>
                            </div>
                            <div className="api-docs-card">
                                <h3>Response body</h3>
                                <pre className="api-docs-pre">
                                    <code>
{`{
  "jobId": "aabbccdd-eeff-0011-2233-445566778899"
}`}
                                    </code>
                                </pre>
                            </div>
                        </div>

                        <div className="api-docs-card api-docs-card--code">
                            <div className="api-docs-code-header">
                                <div className="api-docs-tabs">
                                    <button
                                        type="button"
                                        className={`api-docs-tab ${activeLanguage === 'curl' ? 'api-docs-tab--active' : ''}`}
                                        onClick={() => setActiveLanguage('curl')}
                                    >
                                        cURL
                                    </button>
                                    <button
                                        type="button"
                                        className={`api-docs-tab ${activeLanguage === 'javascript' ? 'api-docs-tab--active' : ''}`}
                                        onClick={() => setActiveLanguage('javascript')}
                                    >
                                        JavaScript
                                    </button>
                                    <button
                                        type="button"
                                        className={`api-docs-tab ${activeLanguage === 'python' ? 'api-docs-tab--active' : ''}`}
                                        onClick={() => setActiveLanguage('python')}
                                    >
                                        Python
                                    </button>
                                    <button
                                        type="button"
                                        className={`api-docs-tab ${activeLanguage === 'csharp' ? 'api-docs-tab--active' : ''}`}
                                        onClick={() => setActiveLanguage('csharp')}
                                    >
                                        C#
                                    </button>
                                </div>
                                <button
                                    type="button"
                                    className="api-docs-copy-btn"
                                    onClick={() => handleCopy(codeSnippetsFile)}
                                >
                                    Copy
                                </button>
                            </div>
                            <pre className="api-docs-pre">
                                <code>{codeSnippetsFile[activeLanguage]}</code>
                            </pre>
                        </div>
                    </section>

                    {/* Models */}
                    <section id="model-download-url" className="api-docs-section">
                        <h2>DownloadFileRequestModel</h2>
                        <table className="api-docs-table">
                            <thead>
                                <tr>
                                    <th>Field</th>
                                    <th>Type</th>
                                    <th>Required</th>
                                    <th>Description</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>url</td>
                                    <td>string</td>
                                    <td>Yes</td>
                                    <td>Remote media URL to download.</td>
                                </tr>
                                <tr>
                                    <td>duration</td>
                                    <td><code className="api-docs-inline-code">DurationModel</code></td>
                                    <td>No</td>
                                    <td>Optional trim range for the media.</td>
                                </tr>
                                <tr>
                                    <td>outputFormat</td>
                                    <td>int (enum)</td>
                                    <td>Yes</td>
                                    <td>Desired output format (see OutputFormatRequestModel).</td>
                                </tr>
                                <tr>
                                    <td>notifyByEmail</td>
                                    <td>bool</td>
                                    <td>No</td>
                                    <td>If true, send an email when processing completes.</td>
                                </tr>
                                <tr>
                                    <td>outputFileName</td>
                                    <td>string</td>
                                    <td>No</td>
                                    <td>Custom base name for the output file.</td>
                                </tr>
                            </tbody>
                        </table>
                    </section>

                    <section id="model-download-upload" className="api-docs-section">
                        <h2>DownloadUploadFileRequestModel</h2>
                        <table className="api-docs-table">
                            <thead>
                                <tr>
                                    <th>Field</th>
                                    <th>Type</th>
                                    <th>Required</th>
                                    <th>Description</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>file</td>
                                    <td>IFormFile</td>
                                    <td>Yes</td>
                                    <td>Uploaded media file. Request is rejected if empty.</td>
                                </tr>
                                <tr>
                                    <td>duration</td>
                                    <td><code className="api-docs-inline-code">DurationModel</code></td>
                                    <td>No</td>
                                    <td>Optional trim range for the media.</td>
                                </tr>
                                <tr>
                                    <td>outputFormat</td>
                                    <td>int (enum)</td>
                                    <td>Yes</td>
                                    <td>Desired output format (see OutputFormatRequestModel).</td>
                                </tr>
                                <tr>
                                    <td>notifyByEmail</td>
                                    <td>bool</td>
                                    <td>No</td>
                                    <td>If true, send an email when processing completes.</td>
                                </tr>
                                <tr>
                                    <td>outputFileName</td>
                                    <td>string</td>
                                    <td>No</td>
                                    <td>Custom base name for the output file.</td>
                                </tr>
                            </tbody>
                        </table>
                    </section>

                    <section id="model-duration" className="api-docs-section">
                        <h2>DurationModel</h2>
                        <table className="api-docs-table">
                            <thead>
                                <tr>
                                    <th>Field</th>
                                    <th>Type</th>
                                    <th>Required</th>
                                    <th>Description</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>start</td>
                                    <td>TimeSpan (string)</td>
                                    <td>Yes</td>
                                    <td>Start of the media segment (e.g. <code className="api-docs-inline-code">"00:00:05"</code>).</td>
                                </tr>
                                <tr>
                                    <td>end</td>
                                    <td>TimeSpan (string)</td>
                                    <td>Yes</td>
                                    <td>End of the media segment (e.g. <code className="api-docs-inline-code">"00:01:30"</code>).</td>
                                </tr>
                            </tbody>
                        </table>
                    </section>

                    <section id="model-output-format" className="api-docs-section">
                        <h2>OutputFormatRequestModel</h2>
                        <table className="api-docs-table">
                            <thead>
                                <tr>
                                    <th>Value</th>
                                    <th>Name</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr><td>0</td><td>MP3</td></tr>
                                <tr><td>1</td><td>MP4</td></tr>
                                <tr><td>2</td><td>WAV</td></tr>
                                <tr><td>3</td><td>AAC</td></tr>
                                <tr><td>4</td><td>MKV</td></tr>
                                <tr><td>5</td><td>WEBM</td></tr>
                                <tr><td>6</td><td>AVI</td></tr>
                                <tr><td>7</td><td>MOV</td></tr>
                                <tr><td>8</td><td>M4A</td></tr>
                            </tbody>
                        </table>
                    </section>

                    {/* Webhook – completed */}
                    <section id="model-webhook" className="api-docs-section">
                        <h2>WebhookFileCompletedPayload</h2>
                        <p>
                            When a job completes successfully, your webhook endpoint receives this payload with{' '}
                            <code className="api-docs-inline-code">eventType</code> set to{' '}
                            <code className="api-docs-inline-code">"file.completed"</code>.
                        </p>

                        <div className="api-docs-card">
                            <pre className="api-docs-pre">
                                <code>
{`{
  "jobId": "aabbccdd-eeff-0011-2233-445566778899",
  "eventType": "file.completed",
  "outputUrl": "https://cdn.yourdomain.com/outputs/aabbccdd-eeff-0011-2233-445566778899.mp3"
}`}
                                </code>
                            </pre>
                        </div>

                        <h3 className="api-docs-subheading">Fields</h3>
                        <table className="api-docs-table">
                            <thead>
                                <tr>
                                    <th>Field</th>
                                    <th>Type</th>
                                    <th>Description</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>jobId</td>
                                    <td>string</td>
                                    <td>Matches the JobId returned by the initial API call.</td>
                                </tr>
                                <tr>
                                    <td>eventType</td>
                                    <td>string</td>
                                    <td>
                                        Always <code className="api-docs-inline-code">file.completed</code> for this payload.
                                    </td>
                                </tr>
                                <tr>
                                    <td>outputUrl</td>
                                    <td>string</td>
                                    <td>
                                        URL to the processed media file.
                                    </td>
                                </tr>
                            </tbody>
                        </table>

                        <div className="api-docs-card api-docs-card--code api-docs-card--webhook">
                            <div className="api-docs-code-header">
                                <span className="api-docs-webhook-label">Webhook example (completed)</span>
                                <button
                                    type="button"
                                    className="api-docs-copy-btn"
                                    onClick={() => handleCopy(webhookCompletedExample)}
                                >
                                    Copy
                                </button>
                            </div>
                            <pre className="api-docs-pre">
                                <code>{webhookCompletedExample}</code>
                            </pre>
                        </div>
                    </section>

                    {/* Webhook – failed */}
                    <section id="model-webhook-failed" className="api-docs-section">
                        <h2>WebhookFileFailedPayload</h2>
                        <p>
                            When a job fails, your webhook endpoint receives this payload with{' '}
                            <code className="api-docs-inline-code">eventType</code> set to{' '}
                            <code className="api-docs-inline-code">"file.failed"</code>.
                        </p>

                        <div className="api-docs-card">
                            <pre className="api-docs-pre">
                                <code>
{`{
  "jobId": "aabbccdd-eeff-0011-2233-445566778899",
  "eventType": "file.failed",
  "failReason": "Transcoding failed due to unsupported codec."
}`}
                                </code>
                            </pre>
                        </div>

                        <h3 className="api-docs-subheading">Fields</h3>
                        <table className="api-docs-table">
                            <thead>
                                <tr>
                                    <th>Field</th>
                                    <th>Type</th>
                                    <th>Description</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>jobId</td>
                                    <td>string</td>
                                    <td>Matches the JobId returned by the initial API call.</td>
                                </tr>
                                <tr>
                                    <td>eventType</td>
                                    <td>string</td>
                                    <td>
                                        Always <code className="api-docs-inline-code">file.failed</code> for this payload.
                                    </td>
                                </tr>
                                <tr>
                                    <td>failReason</td>
                                    <td>string</td>
                                    <td>
                                        Human-readable reason describing why the job failed.
                                    </td>
                                </tr>
                            </tbody>
                        </table>

                        <div className="api-docs-card api-docs-card--code api-docs-card--webhook">
                            <div className="api-docs-code-header">
                                <span className="api-docs-webhook-label">Webhook example (failed)</span>
                                <button
                                    type="button"
                                    className="api-docs-copy-btn"
                                    onClick={() => handleCopy(webhookFailedExample)}
                                >
                                    Copy
                                </button>
                            </div>
                            <pre className="api-docs-pre">
                                <code>{webhookFailedExample}</code>
                            </pre>
                        </div>

                        <p className="api-docs-note">
                            You typically configure your webhook URL in your manage API settings.
                        </p>
                    </section>
                </main>
            </div>
        </div>
    );
};

export default ApiDocsPage;
